<script setup>
import RecordLink from '@/components/record/RecordLink.vue'
import { computed, nextTick, ref, watch } from 'vue'
import { api, NetworkError } from '@/lib/http.js'
import { listRecords } from '@/lib/records.js'
import { rememberLabel } from '@/lib/offline.js'
import { ENTITIES } from '@/lib/entities.js'
import { entityLabel } from '@/lib/labels.js'
import { prefillFromText } from '@/lib/quickCreate.js'
import { useSession } from '@/stores/session.js'
import { useUi } from '@/stores/ui.js'
import { useDebounce } from '@/composables/useDebounce.js'
import { useFloating } from '@/composables/useFloating.js'

/**
 * Pick one record of any of `entities` by typing fragments of it, as the search box of the top bar
 * finds them (D-11): online Core's search, offline the replica of each. v-model is {entity, uuid};
 * `reference` the {entity, uuid, label} shown until something else is picked. Not found: made
 * from here as one of them, with what was typed.
 */
const props = defineProps({
  entities: { type: Array, required: true },
  modelValue: { type: Object, default: null },
  reference: { type: Object, default: null },
  disabled: Boolean,
  invalid: Boolean,
})
const emit = defineEmits(['update:modelValue', 'select'])

const session = useSession()
const ui = useUi()
const query = ref('')
const { open, anchor: root, floating, style, show, close, place } = useFloating()
const width = ref(0)
const listStyle = computed(() => ({ ...style.value, width: `${width.value}px` }))
const loading = ref(false)
const results = ref([])
const active = ref(0)
const chosen = ref(props.reference)
watch(() => props.reference, (v) => {
  if (v) chosen.value = v
})
watch(() => props.modelValue, (v) => {
  if (!v) chosen.value = null
})
const label = computed(() => (props.modelValue ? (chosen.value?.uuid === props.modelValue.uuid ? chosen.value.label : null) || '…' : ''))
const short = computed(() => query.value.trim().length < 2)
// What the results shown were found for: till they are for what is typed, it is still searching.
const found = ref(null)
const searching = computed(() => !short.value && (loading.value || found.value !== query.value))

const search = useDebounce(async () => {
  if (short.value) {
    results.value = []
    return
  }
  const asked = query.value
  loading.value = true
  try {
    let groups
    try {
      groups = (await api('/search', { query: { q: query.value, limit: 5, entities: props.entities.join(',') }, timeoutMs: 8000 })).data
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      groups = []
      for (const entity of props.entities) {
        const res = await listRecords(entity, { filter: { q: query.value }, page: { size: 5 } }, { me: session.me }).catch(() => ({ data: [] }))
        if (res.data.length) groups.push({ entity, items: res.data })
      }
    }
    if (asked !== query.value) return
    results.value = groups.flatMap((g) => g.items.map((r) => ({ ...r, entity: g.entity })))
    found.value = asked
    active.value = 0
    if (open.value) nextTick(place)
  } catch {
    results.value = []
    found.value = asked
  } finally {
    loading.value = false
  }
}, 220)

const creatable = computed(() => (props.disabled ? [] : props.entities.filter((e) => ENTITIES[e] && session.has(`${e}:create`))))
function create(entity) {
  const typed = query.value
  close()
  ui.openCreate({ entity, defaults: prefillFromText(entity, typed), onSaved: (record) => pick({ ...record, entity }) })
}

function start() {
  if (props.disabled) return
  if (!open.value) {
    width.value = root.value?.offsetWidth || 0
    show()
  }
  search()
}
function pick(record) {
  const ref_ = { uuid: record.uuid, label: record.label, number: record.number, entity: record.entity }
  chosen.value = ref_
  rememberLabel(ref_)
  emit('update:modelValue', { entity: record.entity, uuid: record.uuid })
  emit('select', record)
  close()
  query.value = ''
}
function clear() {
  chosen.value = null
  emit('update:modelValue', null)
  emit('select', null)
}
function onKey(e) {
  if (e.key === 'ArrowDown') { active.value = Math.min(active.value + 1, results.value.length - 1); e.preventDefault() }
  else if (e.key === 'ArrowUp') { active.value = Math.max(active.value - 1, 0); e.preventDefault() }
  else if (e.key === 'Enter' && results.value[active.value]) { pick(results.value[active.value]); e.preventDefault() }
  else if (e.key === 'Escape') close()
}
</script>

<template>
  <div ref="root" class="relative">
    <div v-if="modelValue && !open" :class="['input flex items-center gap-2', invalid ? 'invalid' : '', disabled ? 'opacity-70' : '']">
      <i :class="['fa-solid', ENTITIES[modelValue.entity]?.icon, 'text-xs', ENTITIES[modelValue.entity]?.color]" :title="entityLabel(modelValue.entity)" aria-hidden="true"></i>
      <RecordLink :entity="modelValue.entity" :uuid="modelValue.uuid" class="link min-w-0 flex-1 truncate">{{ label }}</RecordLink>
      <button v-if="!disabled" type="button" class="text-faint hover:text-[var(--text)]" :aria-label="$t('common.edit')" @click="start"><i class="fa-solid fa-pen text-xs" aria-hidden="true"></i></button>
      <button v-if="!disabled" type="button" class="text-faint hover:text-red-600" :aria-label="$t('common.clear')" @click="clear"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
    </div>
    <div v-else class="relative">
      <input
        v-model="query"
        type="search"
        autocomplete="off"
        :class="['input pr-8', invalid ? 'invalid' : '']"
        :placeholder="$t('common.searchAnyPlaceholder')"
        :disabled="disabled"
        role="combobox"
        :aria-expanded="open"
        data-testid="any-record-search"
        @focus="start"
        @input="start"
        @keydown="onKey"
      />
      <i :class="['fa-solid absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-faint', loading ? 'fa-circle-notch fa-spin' : 'fa-magnifying-glass']" aria-hidden="true"></i>
    </div>
    <Teleport to="body">
    <div v-if="open" ref="floating" class="surface fixed flex max-h-80 flex-col overflow-hidden rounded-xl border border-default shadow-xl" :style="listStyle" role="listbox">
      <div class="min-h-0 flex-1 overflow-y-auto py-1">
        <p v-if="short" class="px-3 py-2 text-sm text-faint">{{ $t('common.typeTwo') }}</p>
        <p v-else-if="searching && results.length === 0" class="px-3 py-2 text-sm text-faint" data-testid="picker-searching"><i class="fa-solid fa-circle-notch fa-spin mr-1.5 text-xs" aria-hidden="true"></i>{{ $t('common.searching') }}</p>
        <p v-else-if="results.length === 0" class="px-3 py-2 text-sm text-faint">{{ $t('common.nothingFound') }}</p>
        <button
          v-for="(r, i) in results"
          :key="`${r.entity}:${r.uuid}`"
          type="button"
          role="option"
          :aria-selected="i === active"
          :class="['flex w-full items-center gap-2 px-3 py-2 text-left text-sm', i === active ? 'bg-brand-50 dark:bg-brand-900/40' : 'hover:bg-[var(--surface-3)]']"
          :data-testid="`any-record-${r.entity}`"
          @mousedown.prevent="pick(r)"
        >
          <i :class="['fa-solid fa-fw text-xs', ENTITIES[r.entity]?.icon, ENTITIES[r.entity]?.color]" aria-hidden="true"></i>
          <span class="min-w-0 flex-1 truncate">{{ r.label }}</span>
          <span class="shrink-0 text-xs text-faint">{{ entityLabel(r.entity) }}<template v-if="r.number"> · {{ r.number }}</template></span>
        </button>
      </div>
      <!-- Not there yet: made as one of them, with what was typed. -->
      <div v-if="creatable.length && !short" class="flex shrink-0 flex-wrap items-center gap-1.5 border-t border-default px-3 py-2">
        <span class="text-xs text-faint">{{ $t('create.fromSearch', { q: query.trim() }) }}</span>
        <button v-for="e in creatable" :key="e" type="button" class="inline-flex items-center gap-1 rounded-full border border-default px-2.5 py-1 text-xs hover:bg-[var(--surface-3)]" :data-testid="`any-record-create-${e}`" @mousedown.prevent="create(e)">
          <i :class="['fa-solid fa-plus text-3xs', ENTITIES[e].color]" aria-hidden="true"></i>{{ entityLabel(e) }}
        </button>
      </div>
    </div>
    </Teleport>
  </div>
</template>
